/**
 * North Star Decision Intelligence OS
 * Custom React Hooks
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { Pool, PoolMembership, LeaderboardEntry, BSLProfile, PoolWithMembership, getAppConfig } from './types';
import { PoolService, MembershipService, BSLService, fetchUserPoolsWithMemberships, ApiError } from './api';

const config = getAppConfig();
const CACHE_DURATION_MS = 30000;
const POLLING_INTERVAL_MS = 30000;

interface CacheEntry<T> { data: T; timestamp: number; }

function isCacheValid<T>(entry: CacheEntry<T> | null): boolean {
  return entry ? Date.now() - entry.timestamp < CACHE_DURATION_MS : false;
}

interface UseFetchResult<T> {
  data: T | null;
  isLoading: boolean;
  error: string | null;
  refresh: () => void;
  lastUpdated: Date | null;
}

function useFetch<T>(fetchFn: () => Promise<T>, deps: unknown[] = []): UseFetchResult<T> {
  const [data, setData] = useState<T | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);
  const cacheRef = useRef<CacheEntry<T> | null>(null);

  const doFetch = useCallback(async (force = false) => {
    if (!force && isCacheValid(cacheRef.current)) {
      setData(cacheRef.current!.data);
      setIsLoading(false);
      return;
    }
    setIsLoading(true);
    setError(null);
    try {
      const result = await fetchFn();
      cacheRef.current = { data: result, timestamp: Date.now() };
      setData(result);
      setLastUpdated(new Date());
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'An unexpected error occurred');
    } finally {
      setIsLoading(false);
    }
  }, [fetchFn]);

  useEffect(() => { doFetch(); }, [...deps, doFetch]);

  return { data, isLoading, error, refresh: useCallback(() => doFetch(true), [doFetch]), lastUpdated };
}

export function usePools(activeOnly = false): {
  pools: Pool[];
  loading: boolean;
  error: string | null;
  refetch: () => void;
} {
  const result = useFetch(
    useCallback(() => PoolService.getAll(), []), 
    []
  );

  const filteredData = activeOnly && result.data
    ? result.data.filter(p => p.status === 'active')
    : result.data;

  return {
    pools: filteredData || [],
    loading: result.isLoading,
    error: result.error,
    refetch: result.refresh,
  };
}

export function usePool(poolId: string): UseFetchResult<Pool> {
  return useFetch(useCallback(() => PoolService.getById(poolId), [poolId]), [poolId]);
}

export function useUserPools(): UseFetchResult<PoolWithMembership[]> {
  return useFetch(useCallback(() => fetchUserPoolsWithMemberships(), []), []);
}

export function useMembership(poolId: string, userId?: string): UseFetchResult<PoolMembership> {
  return useFetch(
    useCallback(() => userId ? MembershipService.getMembership(poolId, userId) : MembershipService.getCurrentUserMembership(poolId), [poolId, userId]),
    [poolId, userId]
  );
}

interface UseLeaderboardResult extends UseFetchResult<LeaderboardEntry[]> {
  isPollingEnabled: boolean;
  togglePolling: () => void;
}

export function useLeaderboard(poolId: string): UseLeaderboardResult {
  const [data, setData] = useState<LeaderboardEntry[] | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);
  const [isPollingEnabled, setIsPollingEnabled] = useState(!config.disablePolling);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const doFetch = useCallback(async () => {
    try {
      const result = await MembershipService.getLeaderboard(poolId);
      setData(result);
      setLastUpdated(new Date());
      setError(null);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Failed to load leaderboard');
    } finally {
      setIsLoading(false);
    }
  }, [poolId]);

  useEffect(() => { doFetch(); }, [doFetch]);

  useEffect(() => {
    if (config.disablePolling) return;
    if (isPollingEnabled) intervalRef.current = setInterval(doFetch, POLLING_INTERVAL_MS);
    return () => { if (intervalRef.current) clearInterval(intervalRef.current); };
  }, [isPollingEnabled, doFetch]);

  return {
    data, isLoading, error, lastUpdated,
    refresh: useCallback(() => { setIsLoading(true); doFetch(); }, [doFetch]),
    isPollingEnabled,
    togglePolling: useCallback(() => setIsPollingEnabled(p => !p), [])
  };
}

interface UseBSLProfileResult extends UseFetchResult<BSLProfile> { cacheAge: number | null; }

export function useBSLProfile(poolId: string, userId: string): UseBSLProfileResult {
  const [data, setData] = useState<BSLProfile | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);
  const cacheRef = useRef<CacheEntry<BSLProfile> | null>(null);

  const doFetch = useCallback(async (force = false) => {
    if (!force && isCacheValid(cacheRef.current)) {
      setData(cacheRef.current!.data);
      setIsLoading(false);
      return;
    }
    setIsLoading(true);
    setError(null);
    try {
      const result = await BSLService.getProfile(poolId, userId);
      cacheRef.current = { data: result, timestamp: Date.now() };
      setData(result);
      setLastUpdated(new Date());
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Failed to load BSL profile');
    } finally {
      setIsLoading(false);
    }
  }, [poolId, userId]);

  useEffect(() => { if (poolId && userId) doFetch(); }, [poolId, userId, doFetch]);

  return {
    data, isLoading, error, lastUpdated,
    refresh: useCallback(() => doFetch(true), [doFetch]),
    cacheAge: cacheRef.current ? Math.floor((Date.now() - cacheRef.current.timestamp) / 1000) : null
  };
}

interface UseAuthResult {
  userId: string | null;
  isAdmin: boolean;
  login: (userId: string) => void;
  logout: () => void;
  isAuthenticated: boolean;
}

export function useAuth(): UseAuthResult {
  const [userId, setUserId] = useState<string | null>(() => localStorage.getItem('current_user_id'));

  const login = useCallback((id: string) => {
    localStorage.setItem('current_user_id', id);
    setUserId(id);
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem('current_user_id');
    setUserId(null);
  }, []);

  return {
    userId,
    isAdmin: false,  // Use AuthContext for proper admin check from DB
    isAuthenticated: userId !== null,
    login,
    logout
  };
}

export function useCurrentUser() {
  const { userId, login } = useAuth();
  return {
    userId: userId || 'guest',
    setCurrentUser: login
  };
}

export function useIsAdmin(): boolean {
  const { isAdmin } = useAuth();
  return isAdmin;
}

export function usePoolMembership(poolId: string) {
  const { data, isLoading, error, refresh } = useMembership(poolId);
  return {
    membership: data,
    loading: isLoading,
    error,
    refresh
  };
}
